import type { ColorSpec } from './types';

/**
 * Navy color spec — the deep brand navy, built on the `zumthor` palette
 * (tooling/tailwind/theme.css). Same family as the left-nav active state.
 *
 * zumthor 500–700 still read violet-blue, so every emphasis surface (filled
 * bg, active states, tinted text) sits on 800–950; 50–300 carry the light
 * tints and borders. Dark mode keeps the fills deep (800/900) and lifts text
 * to 100–300 so it stays legible on slate.
 */
export const navy: ColorSpec = {
  filled: {
    border: 'border-zumthor-900 dark:border-zumthor-700',
    bg: 'bg-zumthor-800 text-white dark:bg-zumthor-800',
    bgActive: 'bg-zumthor-950 text-white',
    hover: 'hover:bg-zumthor-900 hover:text-white active:bg-zumthor-950 active:text-white',
    hoverDark: 'dark:hover:bg-zumthor-900 dark:hover:text-white dark:active:bg-zumthor-950 dark:active:text-white',
  },
  outline: {
    border: 'border-zumthor-300 dark:border-zumthor-700',
    bg: 'bg-zumthor-50 dark:bg-zumthor-950 text-zumthor-800 dark:text-zumthor-200',
    bgActive: 'bg-zumthor-800 dark:bg-zumthor-800 text-white',
    hover: 'hover:bg-zumthor-100 hover:text-zumthor-900 active:bg-zumthor-200 active:text-zumthor-900',
    hoverDark:
      'dark:hover:bg-zumthor-900 dark:hover:text-zumthor-100 dark:active:bg-zumthor-800 dark:active:text-zumthor-100',
    hoverWhenActive: 'hover:bg-zumthor-900 dark:hover:bg-zumthor-800',
  },
  soft: {
    border: 'border-zumthor-100 dark:border-zumthor-900',
    bg: 'bg-zumthor-50 dark:bg-zumthor-950 text-zumthor-800 dark:text-zumthor-200 font-normal',
    bgActive: 'bg-zumthor-800 dark:bg-zumthor-800 text-white',
    hover: 'hover:bg-zumthor-800 hover:text-white active:bg-zumthor-900 active:text-white',
    hoverDark: 'dark:hover:bg-zumthor-800 dark:hover:text-white dark:active:bg-zumthor-900 dark:active:text-white',
    hoverWhenActive: 'hover:bg-zumthor-900 dark:hover:bg-zumthor-800',
  },
  textFilled: {
    text: 'text-zumthor-800 dark:text-zumthor-300',
    activeBg: 'bg-zumthor-800 text-white dark:bg-zumthor-800',
    hover: 'hover:bg-zumthor-800 hover:text-white active:bg-zumthor-900',
    hoverDark: 'dark:hover:bg-zumthor-800 dark:hover:text-white dark:active:bg-zumthor-900',
  },
  textSoft: {
    text: 'text-zumthor-800 dark:text-zumthor-200 font-normal',
    activeBg: 'bg-zumthor-800 text-white dark:bg-zumthor-800',
    hover: 'hover:bg-zumthor-100 hover:text-zumthor-950 active:bg-zumthor-200',
    hoverDark: 'dark:hover:bg-zumthor-900 dark:hover:text-zumthor-100 dark:active:bg-zumthor-800',
  },
  tooltip: 'bg-zumthor-900 dark:bg-zumthor-800 text-white',
  badge: {
    filled: 'bg-zumthor-800 dark:bg-zumthor-800 text-white',
    soft: 'bg-zumthor-500 dark:bg-zumthor-600 text-white',
  },
  softIcon: 'text-zumthor-800 dark:text-zumthor-200 group-hover/btn:text-current',
  underline: {
    filled:
      '[&_u]:text-zumthor-200 dark:[&_u]:text-zumthor-300 [&_u]:decoration-zumthor-300 dark:[&_u]:decoration-zumthor-400',
    outline:
      '[&_u]:text-zumthor-900 dark:[&_u]:text-zumthor-300 [&_u]:decoration-zumthor-700 dark:[&_u]:decoration-zumthor-400',
    soft: '[&_u]:text-zumthor-900 dark:[&_u]:text-zumthor-300 [&_u]:decoration-zumthor-300 dark:[&_u]:decoration-zumthor-400',
  },
  input: {
    filled: {
      border: 'border-zumthor-900 dark:border-zumthor-700',
      bg: 'bg-zumthor-800 dark:bg-zumthor-800',
      focusBg: 'focus-within:bg-zumthor-900 dark:focus-within:bg-zumthor-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-zumthor-800 dark:focus-within:outline-zumthor-600',
      hoverActive:
        'hover:bg-zumthor-900 hover:text-white active:bg-zumthor-950 active:text-white dark:hover:bg-zumthor-900 dark:active:bg-zumthor-950',
      readOnlyBg: 'bg-zumthor-700 dark:bg-zumthor-900',
      text: 'text-white',
    },
    outline: {
      border: 'border-zumthor-300 dark:border-zumthor-700',
      bg: 'bg-zumthor-50 dark:bg-zumthor-950',
      focusBg: 'focus-within:bg-zumthor-100 dark:focus-within:bg-zumthor-900/50',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-zumthor-300 dark:focus-within:outline-zumthor-700',
      hoverActive:
        'hover:bg-zumthor-100 hover:text-zumthor-900 active:bg-zumthor-200 active:text-zumthor-900 dark:hover:bg-zumthor-900 dark:hover:text-zumthor-100 dark:active:bg-zumthor-800 dark:active:text-zumthor-100',
      readOnlyBg: 'bg-zumthor-50 dark:bg-zumthor-900',
      text: 'text-zumthor-900 dark:text-zumthor-100',
    },
    soft: {
      border: 'border-zumthor-100 dark:border-zumthor-900',
      bg: 'bg-zumthor-50 dark:bg-zumthor-950',
      focusBg: 'focus-within:bg-zumthor-100 dark:focus-within:bg-zumthor-900/50',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-zumthor-100 dark:focus-within:outline-zumthor-900',
      hoverActive:
        'hover:bg-zumthor-100 hover:text-zumthor-950 active:bg-zumthor-200 active:text-zumthor-950 dark:hover:bg-zumthor-900 dark:hover:text-zumthor-100 dark:active:bg-zumthor-800 dark:active:text-zumthor-100',
      readOnlyBg: 'bg-zumthor-50 dark:bg-zumthor-900',
      text: 'text-zumthor-900 dark:text-zumthor-200',
    },
  },
  inputMuted: {
    filled: 'text-white',
    outline: 'text-zumthor-400 dark:text-zumthor-300/50',
    soft: 'text-zumthor-400 dark:text-zumthor-300/50',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-white',
    outline: 'placeholder:text-zumthor-400 dark:placeholder:text-zumthor-300/50',
    soft: 'placeholder:text-zumthor-400 dark:placeholder:text-zumthor-300/50',
  },
  group: {
    filled: 'border-zumthor-300 dark:border-zumthor-800 bg-zumthor-50 dark:bg-zumthor-950',
    outline: 'border-zumthor-300 dark:border-zumthor-700 bg-zumthor-50 dark:bg-zumthor-950',
    soft: 'border-zumthor-200 dark:border-zumthor-800 bg-zumthor-100 dark:bg-zumthor-900',
  },
  groupDivider: {
    filled: 'bg-zumthor-300 dark:bg-zumthor-800',
    outline: 'bg-zumthor-300 dark:bg-zumthor-700',
    soft: 'bg-zumthor-200 dark:bg-zumthor-800',
  },
  segment: {
    filled: 'border-zumthor-300 dark:border-zumthor-800 bg-zumthor-50 dark:bg-zumthor-950',
    outline: 'border-zumthor-300 dark:border-zumthor-700 bg-zumthor-50 dark:bg-zumthor-950',
  },
  segmentDivider: {
    filled: 'bg-zumthor-300 dark:bg-zumthor-800',
    outline: 'bg-zumthor-300 dark:bg-zumthor-700',
  },
  popoverHighlight: 'bg-zumthor-800 text-white',
  popoverText: 'text-zumthor-800 dark:text-zumthor-200',
  popoverBorder: 'border-zumthor-200 dark:border-zumthor-800',
};
